import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useDocumentCacheManager } from 'entities/document/api/hooks/use-document-cache-manager';
import type { ErrorDetails } from '../../../../shared/types';
import {
  getDocumentsQueue,
  postDocumentsQueueGroups,
  putDocumentsQueueGroups,
} from '../../api';
import type { Node } from '../../model';
import { convertNodesToDocumentQueue } from '../helpers';
import { getUsersDocumentIdByGroup } from './helpers';
import { queryKey } from './query-key';
import { usePutDocumentsQueue } from './use-put-documents-queue';

export type UsePutDocumentsWorkflowProps = {
  documentId: number;
  /**
   * Whether the queue-groups feature is available for this document. When it is
   * not (feature gated off / insufficient rights), the group reconcile/create
   * calls below are skipped — the backend would reject them with a 403 that
   * fails the whole save even though the queue itself persisted fine.
   */
  hasGroupsAccess?: boolean;
};

const convertErrorsToWorkflowErrors = (
  nodes: Node[],
  axiosError: AxiosError<any>,
) => {
  const { acceptors, signers } = convertNodesToDocumentQueue(nodes);
  const errors = axiosError.response?.data?.errors || {};

  // key e.g.: signers.0.queue.0.sign_method
  return Object.entries(errors).map(([key, value]: [string, any]) => {
    const pathParts = key.split('.');

    const resource = pathParts[0] === 'signers' ? signers : acceptors;
    const nodeId = resource[pathParts[1]]._path;

    const participantPath = resource[pathParts[1]].queue[pathParts[3]]._path;

    const path = [nodeId, 'participants', participantPath].join('.');

    return {
      code: 'api-error',
      path,
      message: value[0],
    };
  });
};

export class ApiError extends Error {
  errors: ErrorDetails[];

  constructor(message: string, errors: ErrorDetails[]) {
    super(message);
    this.errors = errors;
  }
}

/**
 * Custom hook to put documents queue.
 * Hook convert workflow to the old queue format and put it to the API.
 * After that, it updates groups in the API and updates the local cache.
 *
 * @param props - Hook props.
 */
export const usePutDocumentsWorkflow = (
  props: UsePutDocumentsWorkflowProps,
) => {
  const { documentId, hasGroupsAccess = false } = props;
  const queryClient = useQueryClient();
  const { invalidateDocuments } = useDocumentCacheManager();
  const { mutateAsync: saveDocumentQueue } = usePutDocumentsQueue({
    documentId,
  });

  return useMutation({
    mutationFn: async (nodes: Node[]) => {
      // 0. Convert workflow to the old queue format
      const { acceptors, signers, groups } = convertNodesToDocumentQueue(nodes);
      const queue = { acceptors, signers };

      // 1. Save document queue
      try {
        await saveDocumentQueue({ documentQueue: queue });
      } catch (e: any) {
        const errors = convertErrorsToWorkflowErrors(nodes, e as any);
        throw new ApiError(e.response.data.message, errors);
      }

      // Queue groups are a gated feature. When the document has no access to it,
      // skip steps 2-4 entirely: the group endpoints would 403 and fail the save
      // even though the queue above persisted fine. The designer also hides the
      // grouping UI in that case, so there are never groups to reconcile here.
      if (hasGroupsAccess) {
        // 2. refresh local cache for document groups
        const newDocumentQueueResult: any = await getDocumentsQueue({
          documentId,
        });
        const newDocumentQueue = newDocumentQueueResult.data;

        const convertedGroups = groups.map((group: any) => ({
          id: group.id,
          documentUsers: getUsersDocumentIdByGroup(
            newDocumentQueue as any,
            group,
          ).map(id => ({ id })),
          requiredApprovals: group.requiredApprovals,
          name:
            group.name !== '' && group.name !== null ? group.name : undefined,
        }));

        // 3. Reconcile existing groups. The PUT endpoint treats its payload as a
        // full sync: it updates the listed groups and deletes every other group
        // on the document (an empty list clears them all via deleteAllGroups).
        // It must run BEFORE the POST below — otherwise the reconciliation would
        // delete the groups we are about to create. Always send it: when a group
        // is ungrouped the surviving list can be empty, and that empty PUT is
        // exactly what removes the leftover group on the backend.
        const groupsToUpdate: any[] = convertedGroups.filter(
          (g: any) => !!g.id,
        );
        await putDocumentsQueueGroups({
          documentId,
          groups: groupsToUpdate,
        });

        // 4. create brand-new groups (no id yet)
        const groupsToCreate: any[] = convertedGroups.filter((g: any) => !g.id);
        if (groupsToCreate.length > 0) {
          await postDocumentsQueueGroups({
            documentId,
            groups: groupsToCreate,
          });
        }
      }

      // 5. Invalidate related queries
      // Groups are persisted in steps 3-4, so refresh the groups cache now —
      // the queue mutation above only invalidates the queue, not the groups.
      invalidateDocuments({
        documentIds: [documentId],
        includeQueue: false,
        includeQueueGroup: hasGroupsAccess,
      });
      // Refresh the workflow read so useDocument and the designer observe the
      // just-saved queue/groups.
      await queryClient.refetchQueries(
        queryKey.documentWorkflow(documentId, hasGroupsAccess),
      );

      return queryClient.ensureQueryData(
        queryKey.documentWorkflow(documentId, hasGroupsAccess),
      );
    },
  });
};
